import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Send, UserRound } from "lucide-react";
import {
  applyLeaveRequest,
  fetchLeaveBalances,
  fetchLeaveRequests,
  fetchLeaveTypes,
  fetchMyLeaveEmployee,
} from "../../../Moduels/leave/leaveApi";
import { fetchMyCompOff } from "../../../Moduels/leave/compOffApi";
import DatePickerField from "../../common/DatePickerField";
import { HrAlert, HrBtn } from "../../hr/HrDesignSystem";
import BrixigoPageLoader from "../../common/BrixigoPageLoader";
import GuidedNotice from "../../ui/GuidedNotice";
import ConfirmDialog from "../../ui/ConfirmDialog";
import SuccessScreen from "../../common/SuccessScreen";
import { useLeaveOrg } from "./useLeaveOrg";
import { toDateValue } from "../../common/datePickerUtils";
import { formatIndianDate } from "../../../Moduels/attendance/indianCalendarUtils";
import {
  COMP_OFF_CODE,
  CompOffOverBalanceNotice,
  LeaveRequestsTable,
  compOffExceedsBalance,
  fmtDays,
  leaveDayCount,
  pendingDaysFor,
  requestableLeaveTypes,
  useLeaveDayMarkers,
} from "./leaveFormShared";

const EMPTY_FORM = {
  leave_type_id: "",
  start_date: "",
  end_date: "",
  duration: "full_day", // full_day | half_day
  half_day_session: "",
  reason: "",
};

export default function LvLeaveApply() {
  const navigate = useNavigate();
  const { orgId, loading: orgLoading } = useLeaveOrg();
  const todayISO = toDateValue(new Date());

  const [me, setMe] = useState(null); // { linked, employee_id, name, employee_code, … }
  const [meLoading, setMeLoading] = useState(true);
  const [meError, setMeError] = useState("");
  const [types, setTypes] = useState([]);
  const [balances, setBalances] = useState([]);
  const [compOff, setCompOff] = useState(null);
  const [myRequests, setMyRequests] = useState([]);
  const [form, setForm] = useState(EMPTY_FORM);
  const [fieldErrors, setFieldErrors] = useState({});
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  // True briefly after a confirmed submit: shows the shared success panel.
  const [showSubmitted, setShowSubmitted] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const submittingRef = useRef(false);

  const employeeId = me?.linked ? me.employee_id : null;
  const year = new Date().getFullYear();

  // The leave is always for the logged-in user's own employee record (resolved by the server).
  useEffect(() => {
    let alive = true;
    setMeLoading(true);
    fetchMyLeaveEmployee()
      .then((data) => { if (alive) setMe(data); })
      .catch((e) => { if (alive) setMeError(e.message); })
      .finally(() => { if (alive) setMeLoading(false); });
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    if (!orgId) return;
    fetchLeaveTypes(orgId).then((d) => setTypes(Array.isArray(d) ? d : [])).catch(() => setTypes([]));
  }, [orgId]);

  const loadMine = () => {
    if (!orgId || !employeeId) return;
    fetchLeaveBalances({ organisation_id: orgId, employee_id: employeeId, year })
      .then((d) => setBalances(Array.isArray(d) ? d : []))
      .catch(() => setBalances([]));
    fetchMyCompOff(employeeId)
      .then((data) => setCompOff(data))
      .catch(() => setCompOff(null));
    fetchLeaveRequests({ organisation_id: orgId, employee_id: employeeId })
      .then((d) => setMyRequests(Array.isArray(d) ? d : []))
      .catch(() => setMyRequests([]));
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(loadMine, [orgId, employeeId, year]);

  const applicableTypes = useMemo(() => requestableLeaveTypes(types, me), [types, me]);

  const selectedType = applicableTypes.find((t) => t.leave_type_id === form.leave_type_id) || null;
  const isCompOff = String(selectedType?.code || "").toUpperCase() === COMP_OFF_CODE;
  const allowHalfDay = selectedType?.applicability_json?.allow_half_day !== false;
  const isSingleDay = Boolean(form.start_date && form.end_date && form.start_date === form.end_date);
  const balance = balances.find((b) => b.leave_type_id === form.leave_type_id) || null;
  const compOffAvailable = Number(compOff?.summary?.available || 0);
  const available = isCompOff ? compOffAvailable : (balance ? Number(balance.available || 0) : 0);
  const pendingDays = isCompOff ? 0 : pendingDaysFor(myRequests, form.leave_type_id);
  const dayCount = useMemo(
    () => leaveDayCount(form.start_date, form.end_date, form.duration === "half_day"),
    [form.start_date, form.end_date, form.duration]
  );
  const compOffOverBalance = compOffExceedsBalance(isCompOff && compOff != null, dayCount, available);

  // Half day only for a single day on a leave type that allows it.
  useEffect(() => {
    if (form.duration === "half_day" && (!allowHalfDay || (form.start_date && form.end_date && !isSingleDay))) {
      setForm((f) => ({ ...f, duration: "full_day", half_day_session: "" }));
    }
  }, [allowHalfDay, isSingleDay, form.duration, form.start_date, form.end_date]);

  const dayMarkers = useLeaveDayMarkers(orgId, myRequests);

  const setField = (k, v) => {
    setForm((f) => ({ ...f, [k]: v }));
    setFieldErrors((fe) => (fe[k] ? { ...fe, [k]: undefined } : fe));
    setError("");
    setSuccess("");
  };

  const validate = () => {
    const errs = {};
    if (!form.leave_type_id) errs.leave_type_id = "Select a leave type.";
    if (!form.start_date) errs.start_date = "Select the From date.";
    if (!form.end_date) errs.end_date = "Select the To date.";
    if (form.start_date && form.end_date && form.end_date < form.start_date) {
      errs.end_date = "To date cannot be before From date.";
    }
    if (form.duration === "half_day") {
      if (!allowHalfDay) errs.duration = `Half day is not allowed for ${selectedType?.name || "this leave type"}.`;
      else if (!isSingleDay) errs.duration = "Half day applies only when From and To are the same date.";
      else if (!form.half_day_session) errs.half_day_session = "Select First Half or Second Half.";
    }
    if (compOffOverBalance) {
      errs.end_date = `Comp Off balance is ${fmtDays(available)}. This request is ${fmtDays(dayCount)}.`;
    }
    const maxConsecutive = Number(selectedType?.applicability_json?.max_consecutive_days) || 0;
    if (maxConsecutive > 0 && dayCount && dayCount > maxConsecutive) {
      errs.end_date = `Maximum ${fmtDays(maxConsecutive)} in a row is allowed for ${selectedType.name}.`;
    }
    return errs;
  };

  const handleSubmitClick = (e) => {
    e.preventDefault();
    if (submittingRef.current) return;
    const errs = validate();
    setFieldErrors(errs);
    if (Object.keys(errs).length) {
      if (!compOffOverBalance) setError("Please fix the highlighted fields.");
      return;
    }
    setShowConfirm(true);
  };

  const confirmSubmit = async () => {
    if (submittingRef.current) return;
    submittingRef.current = true;
    setShowConfirm(false);
    setSubmitting(true);
    setError("");
    setSuccess("");
    try {
      // No employee_id: the server applies for the logged-in user's own employee record.
      await applyLeaveRequest({
        organisation_id: orgId,
        leave_type_id: form.leave_type_id,
        start_date: form.start_date,
        end_date: form.end_date,
        reason: form.reason.trim() || null,
        half_day: form.duration === "half_day",
        half_day_session: form.duration === "half_day" ? form.half_day_session : null,
      });
      setSuccess("Leave request submitted. You can track its approval status below.");
      setShowSubmitted(true);
      setForm((f) => ({ ...EMPTY_FORM, leave_type_id: f.leave_type_id }));
      setFieldErrors({});
      loadMine();
    } catch (err) {
      setError(err.message || "Leave request could not be submitted.");
    } finally {
      submittingRef.current = false;
      setSubmitting(false);
    }
  };

  const handleCancel = () => {
    if (window.history.length > 1) navigate(-1);
    else navigate("/leave");
  };

  if (orgLoading || meLoading) return <BrixigoPageLoader compact message="Loading…" />;

  if (meError) {
    return <GuidedNotice domain="leave" variant="error" message={meError} />;
  }

  if (!me?.linked) {
    return (
      <div className="hr-form-section p-5 sm:p-6">
        <HrAlert variant="warning">
          {me?.message ||
            "Your login is not linked to an employee profile, so leave cannot be applied. Ask an administrator to link it."}
        </HrAlert>
      </div>
    );
  }

  const fieldError = (k) => (fieldErrors[k] ? <p className="text-xs text-rose-600 mt-1">{fieldErrors[k]}</p> : null);

  return (
    <div className="space-y-5">
      {showSubmitted ? (
        // The page stays open: brief confirmation, then the form returns (no redirect).
        <SuccessScreen
          variant="panel"
          title="Leave request submitted successfully!"
          message="Track its approval status in My Leave Requests below."
          redirecting={false}
          onComplete={() => setShowSubmitted(false)}
        />
      ) : (
      <form onSubmit={handleSubmitClick} className="hr-form-section p-4 sm:p-6 space-y-4" noValidate>
        {/* Employee — the logged-in user's own record, read-only */}
        <div>
          <span className="hr-form-label">Employee</span>
          <div className="hr-input flex items-center gap-2 bg-slate-50 text-slate-800 font-semibold">
            <UserRound size={15} className="text-slate-500 shrink-0" />
            <span className="truncate">
              {me.name}
              {me.employee_code ? ` (${me.employee_code})` : ""}
            </span>
          </div>
        </div>

        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className="hr-form-label" htmlFor="lv-type">
              Leave Type <span className="text-rose-500">*</span>
            </label>
            <select
              id="lv-type"
              className="hr-select"
              value={form.leave_type_id}
              onChange={(e) => setField("leave_type_id", e.target.value)}
              aria-invalid={Boolean(fieldErrors.leave_type_id)}
            >
              <option value="">Select leave type</option>
              {applicableTypes.map((t) => (
                <option key={t.leave_type_id} value={t.leave_type_id}>
                  {t.name} ({t.code}){t.is_paid ? "" : " — Unpaid"}
                </option>
              ))}
            </select>
            {applicableTypes.length === 0 && (
              <p className="text-xs text-slate-500 mt-1">No leave types are available to you yet.</p>
            )}
            {fieldError("leave_type_id")}
          </div>

          <div>
            <span className="hr-form-label">Available Balance</span>
            <div className={`hr-input font-semibold ${compOffOverBalance ? "border-amber-400 bg-amber-50 text-amber-950" : "bg-slate-50 text-slate-800"}`}>
              {!form.leave_type_id
                ? "Select a leave type"
                : isCompOff
                  ? fmtDays(compOffAvailable)
                  : balance
                    ? fmtDays(available)
                    : "No balance allocated"}
            </div>
            {form.leave_type_id && pendingDays > 0 && (
              <p className="text-xs text-slate-600 mt-1">{fmtDays(pendingDays)} already in pending requests.</p>
            )}
          </div>

          <DatePickerField
            label="From Date"
            required
            value={form.start_date}
            onChange={(date) => setField("start_date", date)}
            placeholder="DD-MM-YYYY"
            min={todayISO || undefined}
            max={form.end_date || undefined}
            dayMarkers={dayMarkers}
            error={fieldErrors.start_date}
          />
          <DatePickerField
            label="To Date"
            required
            value={form.end_date}
            onChange={(date) => setField("end_date", date)}
            placeholder="DD-MM-YYYY"
            min={form.start_date || todayISO || undefined}
            dayMarkers={dayMarkers}
            error={fieldErrors.end_date}
          />
          {(fieldErrors.start_date || fieldErrors.end_date) && (
            <div className="sm:col-span-2 -mt-2">
              {fieldError("start_date")}
              {fieldError("end_date")}
            </div>
          )}

          <div>
            <label className="hr-form-label" htmlFor="lv-duration">
              Duration <span className="text-rose-500">*</span>
            </label>
            <select
              id="lv-duration"
              className="hr-select"
              value={form.duration}
              onChange={(e) => {
                setField("duration", e.target.value);
                if (e.target.value === "full_day") setField("half_day_session", "");
              }}
            >
              <option value="full_day">Full Day</option>
              {allowHalfDay && (!form.start_date || !form.end_date || isSingleDay) && (
                <option value="half_day">Half Day</option>
              )}
            </select>
            {fieldError("duration")}
            {!fieldErrors.duration && selectedType && !allowHalfDay && (
              <p className="text-xs text-slate-500 mt-1">Half day is not available for {selectedType.name}.</p>
            )}
            {!fieldErrors.duration && allowHalfDay && form.start_date && form.end_date && !isSingleDay && (
              <p className="text-xs text-slate-500 mt-1">Half day applies only when From and To are the same date.</p>
            )}
            {dayCount != null && (
              <p className={`text-xs font-semibold mt-1 ${compOffOverBalance ? "text-amber-800" : "text-slate-700"}`}>
                Total: {fmtDays(dayCount)}
                {compOffOverBalance ? ` · balance ${fmtDays(available)}` : ""}
              </p>
            )}
          </div>

          {form.duration === "half_day" && (
            <div>
              <label className="hr-form-label" htmlFor="lv-session">
                Half-Day Session <span className="text-rose-500">*</span>
              </label>
              <select
                id="lv-session"
                className="hr-select"
                value={form.half_day_session}
                onChange={(e) => setField("half_day_session", e.target.value)}
              >
                <option value="">Select session</option>
                <option value="first_half">First Half</option>
                <option value="second_half">Second Half</option>
              </select>
              {fieldError("half_day_session")}
            </div>
          )}
        </div>

        <div>
          <label className="hr-form-label" htmlFor="lv-reason">Reason / Remarks</label>
          <textarea
            id="lv-reason"
            className="hr-input min-h-[4.5rem] resize-y"
            rows={3}
            placeholder="Brief reason for leave…"
            value={form.reason}
            onChange={(e) => setField("reason", e.target.value)}
          />
        </div>

        {compOffOverBalance ? (
          <CompOffOverBalanceNotice available={available} requested={dayCount} who="You" />
        ) : null}

        {error && <GuidedNotice domain="leave" message={error} variant="error" onDismiss={() => setError("")} />}
        {success && <HrAlert variant="success">{success}</HrAlert>}

        <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-3 pt-2 border-t border-slate-100">
          <HrBtn type="button" variant="secondary" onClick={handleCancel} disabled={submitting}>
            Cancel
          </HrBtn>
          <HrBtn type="submit" variant="primary" disabled={submitting || !orgId || compOffOverBalance}>
            <Send className="w-4 h-4" />
            {submitting ? "Submitting…" : "Submit Leave Request"}
          </HrBtn>
        </div>
      </form>
      )}

      {/* Status tracking for the user's own requests */}
      <section className="hr-form-section p-4 sm:p-6">
        <h3 className="text-base font-bold text-slate-800 mb-3">My Leave Requests</h3>
        <LeaveRequestsTable requests={myRequests} emptyText="You have not applied for any leave yet." />
      </section>

      <ConfirmDialog
        open={showConfirm}
        title="Submit leave request?"
        message={
          selectedType
            ? `Submit ${selectedType.name} (${selectedType.code}) for ${formatIndianDate(form.start_date)} → ${formatIndianDate(
                form.end_date
              )}${dayCount ? ` (${fmtDays(dayCount)})` : ""}?`
            : "Submit this leave request?"
        }
        confirmLabel="Submit"
        loading={submitting}
        onConfirm={confirmSubmit}
        onCancel={() => setShowConfirm(false)}
      />
    </div>
  );
}
